import type { ApolloError } from '@apollo/client';
import { type ReactElement, useMemo } from 'react';
import type { NonUndefined } from 'react-hook-form';

import Loader from './Loader/Loader';
import Message from './Message';
import type { LoaderAppearance, LoaderVariant } from './Loader/Loader.props';
import type { ErrorVariant } from './errors/errorLayouts';
import { ErrorView } from './errors/ErrorView';

type DuplicatedTypes = 'data';

export type TFallback = () => ReactElement;
type StandardOutputProps<OUTPUT> =
  | { loaded: false; data: undefined; Fallback: TFallback }
  | { loaded: true; data: NonUndefined<OUTPUT>; Fallback: undefined };

export type ErrorHandlingOutput<QUERY_OUTPUT> = QUERY_OUTPUT extends {
  data: infer OUTPUT;
}
  ? Omit<QUERY_OUTPUT, DuplicatedTypes> & StandardOutputProps<OUTPUT>
  : never;

export type QueryOutputBase = {
  loading: boolean;
  error?: ApolloError;
  data: unknown;
};

export type ErrorHandlingOptions = {
  // Unified layout variant for both loader and error
  loaderSizeVariant?: LoaderVariant;
  loaderAppearance?: LoaderAppearance;
  errorVariant?: ErrorVariant;
  // Toggles
  disableNoDataFallback?: boolean;
};

export const useDefaultErrorHandling = <QUERY_OUTPUT extends QueryOutputBase>(
  input: QUERY_OUTPUT,
  options: ErrorHandlingOptions = {}
): ErrorHandlingOutput<QUERY_OUTPUT> =>
  useMemo((): ErrorHandlingOutput<QUERY_OUTPUT> => {
    const { loading, error, data, ...rest } = input;

    if (loading) {
      return {
        ...rest,
        loaded: false,
        data: undefined,
        Fallback: (): ReactElement => (
          <Loader variant={options.loaderSizeVariant} appearance={options.loaderAppearance} />
        ),
      } as ErrorHandlingOutput<QUERY_OUTPUT>;
    }

    if (error) {
      return {
        ...rest,
        loaded: false,
        data: undefined,
        error,
        Fallback: () => <ErrorView cause={error} variant={options.errorVariant} />,
      } as ErrorHandlingOutput<QUERY_OUTPUT>;
    }

    if (!data && !options.disableNoDataFallback) {
      return {
        ...rest,
        loaded: false,
        data: undefined,
        Fallback: () => <Message>No data</Message>,
      } as ErrorHandlingOutput<QUERY_OUTPUT>;
    }

    return {
      ...rest,
      loaded: true,
      data: data as NonUndefined<QUERY_OUTPUT['data']>,
      Fallback: undefined,
    } as ErrorHandlingOutput<QUERY_OUTPUT>;
  }, [input, options]);
